import React, { useMemo, useState } from 'react';
import classNames from 'classnames';

import {
  VISIT_DURATION_MINUTES,
  VISIT_MAX_ATTENDEES,
  buildVisitRequest,
  formatVisitMessage,
  parseVisitRequest,
  upcomingVisitDays,
  visitCalendarEvent,
  visitDayParts,
  visitSlotsForDay,
} from '../../../util/visitRequest';
import { AddToCalendar } from '../../../components';
import { ErrorMessage } from '../InquiryForm/InquiryForm';
import IconVisitCalendar from './IconVisitCalendar';

import css from './VisitRequestPanel.module.css';

// Class for the Modal container: narrower than the default marketplace modal.
export const visitModalContainerClassName = css.modalContainer;

const IconSuccess = () => (
  <svg
    className={css.successIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 13l4 4L19 7" />
  </svg>
);

/**
 * Content of the "Planifier une visite" modal on the listing page.
 * Only the slots opened by the advertiser (publicData.visitAvailability) are offered.
 * The request is sent as an inquiry: the slot goes to protectedData.visitRequest
 * and a readable summary is posted as the first message.
 *
 * @param {Object} props
 * @param {Object} props.availability normalized advertiser availability
 * @param {string} props.listingTitle
 * @param {string} props.authorDisplayName
 * @param {string} props.timeZone IANA time zone of the listing
 * @param {string} [props.address]
 * @param {string} [props.listingUrl]
 * @param {Function} props.onSubmit ({ visitRequest, message }) => Promise<txId>
 * @param {Function} props.onViewConversation (txId) => void
 * @param {boolean} props.inProgress
 * @param {Object} [props.sendInquiryError]
 */
const VisitRequestPanel = props => {
  const {
    availability,
    listingTitle,
    authorDisplayName,
    timeZone,
    address,
    listingUrl,
    onSubmit,
    onViewConversation,
    inProgress,
    sendInquiryError,
  } = props;

  const days = useMemo(() => upcomingVisitDays(availability, timeZone), [availability, timeZone]);

  const [dayKey, setDayKey] = useState(days[0] || null);
  const [time, setTime] = useState(null);
  const [attendees, setAttendees] = useState(1);
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState(null);

  const slots = useMemo(() => (dayKey ? visitSlotsForDay(availability, dayKey, timeZone) : []), [
    availability,
    dayKey,
    timeZone,
  ]);

  const visitRequest =
    dayKey && time ? buildVisitRequest({ dayKey, time, timeZone, attendees }) : null;

  const handleSelectDay = key => {
    setDayKey(key);
    if (time && !visitSlotsForDay(availability, key, timeZone).includes(time)) {
      setTime(null);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!visitRequest || inProgress) return;
    const message = formatVisitMessage({
      label: visitRequest.label,
      attendees,
      listingTitle,
      note,
    });
    onSubmit({ visitRequest, message })
      .then(txId => setSent({ txId, visitRequest }))
      .catch(() => {
        // The error is shown above the submit button.
      });
  };

  const header = (
    <div className={css.header}>
      <span className={css.headerBadge}>
        <IconVisitCalendar className={css.headerIcon} />
      </span>
      <div className={css.headerText}>
        <h2 className={css.title}>Planifier une visite</h2>
        <p className={css.subtitle}>
          {listingTitle}
          {authorDisplayName ? ` · ${authorDisplayName}` : null}
        </p>
      </div>
    </div>
  );

  if (sent) {
    const visit = parseVisitRequest({ visitRequest: sent.visitRequest });
    const event = visitCalendarEvent({
      visit,
      listingTitle,
      address,
      withName: authorDisplayName,
      url: listingUrl,
      uid: sent.txId?.uuid,
    });
    return (
      <div className={css.root}>
        <div className={css.success}>
          <div className={css.successBadge}>
            <IconSuccess />
          </div>
          <h2 className={css.successTitle}>Demande envoyée</h2>
          <p className={css.successText}>
            {authorDisplayName} va confirmer ce créneau dans votre messagerie Cobuco.
          </p>
          <div className={css.summary}>
            <IconVisitCalendar className={css.summaryIcon} />
            <span>{visit.label}</span>
          </div>
        </div>
        <AddToCalendar event={event} />
        <button type="button" className={css.submit} onClick={() => onViewConversation(sent.txId)}>
          Voir la conversation
        </button>
      </div>
    );
  }

  if (days.length === 0) {
    return (
      <div className={css.root}>
        {header}
        <p className={css.empty}>
          Aucun créneau de visite n'est disponible pour le moment. Écrivez à l'annonceur pour
          convenir d'un rendez-vous.
        </p>
      </div>
    );
  }

  return (
    <form className={css.root} onSubmit={handleSubmit}>
      {header}

      <section className={css.step}>
        <h3 className={css.stepTitle}>Jour</h3>
        <div className={css.dayScroller} role="listbox" aria-label="Jours disponibles">
          {days.map(key => {
            const { weekday, day, month } = visitDayParts(key, timeZone);
            const isSelected = key === dayKey;
            return (
              <button
                key={key}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={classNames(css.day, { [css.daySelected]: isSelected })}
                onClick={() => handleSelectDay(key)}
              >
                <span className={css.dayWeekday}>{weekday}</span>
                <span className={css.dayNumber}>{day}</span>
                <span className={css.dayMonth}>{month}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className={css.step}>
        <h3 className={css.stepTitle}>
          Horaire <span className={css.stepHint}>· {VISIT_DURATION_MINUTES} min</span>
        </h3>
        <div className={css.slotGrid}>
          {slots.map(slot => (
            <button
              key={slot}
              type="button"
              className={classNames(css.slot, { [css.slotSelected]: time === slot })}
              aria-pressed={time === slot}
              onClick={() => setTime(slot)}
            >
              {slot}
            </button>
          ))}
        </div>
      </section>

      <section className={css.options}>
        <div className={css.attendeesRow}>
          <span className={css.attendeesLabel}>Participants</span>
          <div className={css.stepper}>
            <button
              type="button"
              className={css.stepperBtn}
              onClick={() => setAttendees(n => Math.max(1, n - 1))}
              disabled={attendees <= 1}
              aria-label="Retirer un participant"
            >
              −
            </button>
            <span className={css.stepperValue} aria-live="polite">
              {attendees}
            </span>
            <button
              type="button"
              className={css.stepperBtn}
              onClick={() => setAttendees(n => Math.min(VISIT_MAX_ATTENDEES, n + 1))}
              disabled={attendees >= VISIT_MAX_ATTENDEES}
              aria-label="Ajouter un participant"
            >
              +
            </button>
          </div>
        </div>
        {showNote ? (
          <textarea
            className={css.note}
            rows={2}
            maxLength={1000}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Un message pour l'annonceur (facultatif)"
            aria-label="Message à l'annonceur"
            autoFocus
          />
        ) : (
          <button type="button" className={css.addNote} onClick={() => setShowNote(true)}>
            + Ajouter un message
          </button>
        )}
      </section>

      <div className={css.footer}>
        <ErrorMessage error={sendInquiryError} />
        {sendInquiryError ? (
          <p className={css.errorDetail}>
            Code :{' '}
            {[sendInquiryError.status, sendInquiryError.apiErrors?.[0]?.code]
              .filter(Boolean)
              .join(' · ') ||
              sendInquiryError.message ||
              'inconnu'}
          </p>
        ) : null}
        <button type="submit" className={css.submit} disabled={!visitRequest || inProgress}>
          {inProgress
            ? 'Envoi en cours…'
            : visitRequest
            ? `Demander la visite · ${visitDayParts(dayKey, timeZone).weekday} ${
                visitDayParts(dayKey, timeZone).day
              } à ${time}`
            : 'Choisissez un horaire'}
        </button>
        <p className={css.footerHint}>Gratuit et sans engagement · confirmation par message</p>
      </div>
    </form>
  );
};

export default VisitRequestPanel;
